import React, { useEffect, useState } from "react";
import style from "./AnetModal.module.scss";
import Modal from "../../../../components/modal";
import Button from "../../../../components/new-button";
import toast from "react-hot-toast";
import { updateAnetApplicationStatus, getAnetConfig } from "../../../../../api";
import { extractErrorMessage } from "../../../../utils/extract-error-message";
import TextArea from "../../../../components/form/text-area";
import FancyRadioInput from "../../../../components/form/fancy-radio-input";
import HeadingWithLine from "../../../../components/HeadingWithLine/HeadingWithLine";

const DEFAULT_STATUS_OPTIONS = [
  { value: "Declined", label: "Declined" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Fraud", label: "Fraud" },
];

// Fallback only, used if getAnetConfig() fails - keep in sync with config/anet.php's status_transitions.
const DEFAULT_STATUS_TRANSITIONS = {
  New: ["InProgress", "Cancelled"],
  InProgress: ["Underwriting", "Declined", "Cancelled", "Fraud"],
  Underwriting: ["Declined", "Fraud"],
};

const MIN_REASON_LENGTH = 10;
const REASON_PATTERN = /^[A-Za-z0-9 .,-]+$/;
const REASON_CHARS_MESSAGE = "Use only letters, numbers, spaces, periods, commas and hyphens.";

const isReachable = (from, to, transitions) => {
  if (from === to) return true;

  const visited = new Set([from]);
  const queue = [from];

  while (queue.length) {
    const current = queue.shift();
    for (const next of transitions[current] || []) {
      if (next === to) return true;
      if (!visited.has(next)) {
        visited.add(next);
        queue.push(next);
      }
    }
  }

  return false;
};

function AnetStatusModal({ isOpen, onClose, appData, anetData, onSuccess }) {
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);
  const [statusTransitions, setStatusTransitions] = useState(DEFAULT_STATUS_TRANSITIONS);

  useEffect(() => {
    if (!isOpen) return;

    getAnetConfig()
      .then((response) => {
        if (response.status && response.data.status_transitions) {
          setStatusTransitions(response.data.status_transitions);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch ANET config, using defaults:", error);
      });
  }, [isOpen]);

  const allowedStatuses = statusOptions
    .map((option) => option.value)
    .filter((value) => isReachable(anetData?.anet_status, value, statusTransitions));

  useEffect(() => {
    if (status && !allowedStatuses.includes(status)) {
      setStatus("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anetData?.anet_status, statusTransitions]);

  const handleUpdateStatus = async () => {
    if (!status) {
      toast.error("Please select an outcome");
      return;
    }

    if (reason.trim().length < MIN_REASON_LENGTH) {
      toast.error(`Reason must be at least ${MIN_REASON_LENGTH} characters`);
      return;
    }

    if (!REASON_PATTERN.test(reason.trim())) {
      toast.error(REASON_CHARS_MESSAGE);
      return;
    }

    setLoading(true);
    const toastId = toast.loading("Submitting...");

    try {
      const response = await updateAnetApplicationStatus(appData.id, {
        status,
        reason: reason.trim(),
      });

      if (response.status) {
        toast.success("Application updated successfully!", { id: toastId });
        onSuccess && onSuccess();
        onClose();
      } else {
        toast.error(response.message || "Update failed", { id: toastId });
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to update application"), { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      header={`Decline Application`}
      isOpen={isOpen}
      onClose={onClose}
      size={"md"}
      customClass={style.modal_parent}
    >
      <div className={style.modal_content}>
        <div className={style.detail_box}>
          <p className={style.warning_text}>
            This will update the application in both ANET and your system for
            Application #{anetData?.anet_application_id}. Make sure you want to proceed.
          </p>
        </div>

        <div className={style.section}>
          <HeadingWithLine text="Outcome" margin={false} />
          <div className={style.processor_options_row}>
            {statusOptions.map((option) => (
              <FancyRadioInput
                key={option.value}
                id={`anet-status-${option.value}`}
                name="status"
                label={option.label}
                value={option.value}
                checked={status === option.value}
                disabled={!allowedStatuses.includes(option.value)}
                onChange={(e) => setStatus(e.target.value)}
              />
            ))}
          </div>
        </div>

        <div className={style.section}>
          <HeadingWithLine text="Reason" margin={false} />
          <TextArea
            id="anet-status-reason"
            name="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Please provide a reason..."
            rows={4}
            touched={reason.length > 0}
            errored={reason.trim().length < MIN_REASON_LENGTH || !REASON_PATTERN.test(reason.trim())}
            error={
              reason.trim().length < MIN_REASON_LENGTH
                ? `Reason must be at least ${MIN_REASON_LENGTH} characters`
                : REASON_CHARS_MESSAGE
            }
          />
        </div>

        <footer className={style.footer}>
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpdateStatus}
            disabled={
              loading ||
              !status ||
              reason.trim().length < MIN_REASON_LENGTH ||
              !REASON_PATTERN.test(reason.trim())
            }
          >
            {loading ? "Submitting..." : "Confirm"}
          </Button>
        </footer>
      </div>
    </Modal>
  );
}

export default AnetStatusModal;
